import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, InputSignalWithTransform, Signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { faLock, faMinus, faPlus, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { ImageUploadComponent, provideImageUploadTranslationScope } from '@shared/components/upload/image-upload.component';
import { OgsDisabledDirective } from '@shared/directives/disabled/ogs-disabled.directive';
import { provideTranslationScope, TranslationPipeModule, TranslationStoreService } from '@ogs-gmbh/ngx-translate';
import { ProjectDurationComponent } from '@shared/components/output/duration.component';
import { LocalizedDatepickerDirective } from '@shared/directives/localized-datepicker/localized-datepicker.directive';
import { CostCenterRegion } from '@core/services/http/http.service';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, tap } from 'rxjs';
import { RegionDto } from '@core/types/common.types';
import { ProjectFormValue } from '@core/forms/project.form.types';
import { GroupOf } from '@core/forms/form-typing.util';
import { PROJECT_NUMBER } from '@features/project/constants/routes.constant';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { CommonLookupService } from '@core/services/common-lookup/common-lookup.service';
import { STATUS_TYPES } from '@shared/types/status.types';
import { ComponentToastService } from '@shared/services/component-toast/component-toast.service';
import { ProjectDetailsDataService } from './services/project-details.service';
import { FormStateService } from '@core/services/form-state.service';
import { FormFooterComponent } from '@shared/components/form-footer/form-footer.component';
import { Country } from '@shared/services/country/types/country.type';
import { CountrySelectComponent } from "@shared/components/country-select/country-select.component";
import { MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { DynamicFormFieldListComponent } from "@shared/components/dynamic-list/dynamic-form-field-list.component";
import { AllowedFormControlType, FormOf } from '@shared/types/form-of.type';
import { parseProjectDetailsFromForm, parseToProjectDetailsForm, ProjectDetailsForm } from './types/project-details-form.type';
import { FORM_FIELD_ERRORS } from '@shared/consts/form-field-errors.const';
import {
  DynamicFormFieldListCreateFormControl
} from '@shared/components/dynamic-list/types/dynamic-form-field-list-create-form-control.type';
import { ProjectDetails } from './types/project-details.type';
import { OgsItemDirective } from '@shared/directives/ogs-item.directive';
import { RegionSelectComponent } from '@shared/components/region-select/region-select.component';
import { BaseSelectComponent } from '@shared/components/base-select/base-select.component';
import { BaseSelectGetItems } from '@shared/components/base-select/types/base-select-get-items.type';
import { PageableFetchOptions } from '@shared/types/api/pageable-options.type';
import { isEqual } from 'es-toolkit';
import { DirtyCheckDirective } from "@shared/directives/dirty-check/dirty-check.directive";
import { PROJECT_STATUS_TYPES, ProjectStatus } from '@features/project/types/project-status.type';
import { MatSelectModule } from "@angular/material/select";
import { ProjectHeaderService } from '../../header/project-header/services/project-header.service';

const createAdditionalStreetControl: DynamicFormFieldListCreateFormControl<string> = (): FormControl<string> => new FormControl<string>("", { nonNullable: true });
const createAdditionalEmailControl: DynamicFormFieldListCreateFormControl<string> = (): FormControl<string> => new FormControl<string>("", {
  nonNullable: true,
  // eslint-disable-next-line @tseslint/unbound-method
  validators: [ Validators.email ]
});

function buildStreetsArray (values: string[]): FormArray<FormControl<string>> {
  // eslint-disable-next-line @tseslint/unbound-method
  const mainControl: FormControl<string> = new FormControl<string>("", { nonNullable: true, validators: [ Validators.required ] });
  const additionalControls: Array<FormControl<string>> = values.slice(1).map(() => createAdditionalStreetControl());

  return new FormArray<FormControl<string>>([ mainControl, ...additionalControls ]);
}

function buildEmailsArray (values: string[]): FormArray<FormControl<string>> {
  return new FormArray<FormControl<string>>(values.map(() => createAdditionalEmailControl()));
}

export type PreservedFormType = AllowedFormControlType | Country;

const TRANSLATION_SCOPE: string = "feature/project-details";

@Component({
  selector: 'app-project-details',
  standalone: true,
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideTranslationScope(TRANSLATION_SCOPE),
    provideImageUploadTranslationScope(TRANSLATION_SCOPE),
    ProjectDetailsDataService
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    IconModule,
    MatButtonModule,
    RegionSelectComponent,
    ImageUploadComponent,
    OgsDisabledDirective,
    TranslationPipeModule,
    LocalizedDatepickerDirective,
    ProjectDurationComponent,
    BaseSelectComponent,
    FormFooterComponent,
    CountrySelectComponent,
    MatAutocompleteOrigin,
    DynamicFormFieldListComponent,
    OgsItemDirective,
    DirtyCheckDirective,
    MatSelectModule
  ]
})
export class ProjectDetailsComponent {
  private readonly _formBuilder: NonNullableFormBuilder = inject(NonNullableFormBuilder);

  private readonly _formStateService: FormStateService = inject(FormStateService);

  private readonly _projectHeaderService: ProjectHeaderService = inject(ProjectHeaderService);

  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  protected readonly FORM_FIELD_ERRORS: typeof FORM_FIELD_ERRORS = FORM_FIELD_ERRORS;

  protected readonly PROJECT_STATUS_TYPES: typeof PROJECT_STATUS_TYPES = PROJECT_STATUS_TYPES;

  private readonly _router: Router = inject(Router);

  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _toastService: ComponentToastService = inject(ComponentToastService);

  private readonly _route: ActivatedRoute = inject(ActivatedRoute);

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  private readonly _detailsDataService: ProjectDetailsDataService = inject(ProjectDetailsDataService);

  // eslint-disable-next-line @tseslint/typedef
  protected readonly createAdditionalStreetControl = createAdditionalStreetControl;

  // eslint-disable-next-line @tseslint/typedef
  protected readonly createAdditionalEmailControl = createAdditionalEmailControl;

  public readonly _projectNumber: string = inject(PROJECT_NUMBER);

  private readonly lookup: CommonLookupService = inject(CommonLookupService);

  public readonly wasCreated: InputSignalWithTransform<boolean, (boolean | undefined)> = input.required({
    // eslint-disable-next-line @unicorn/prefer-native-coercion-functions
    transform: (wasCreated: boolean | undefined) => Boolean(wasCreated)
  });

  /* eslint-disable @tseslint/unbound-method */
  readonly form: FormOf<ProjectDetailsForm, PreservedFormType> = this._formBuilder.group({
    number: new FormControl({ value: "", disabled: true }, { nonNullable: true, validators: [ Validators.required ] }),
    name: new FormControl("", { nonNullable: true, validators: [ Validators.required ] }),
    status: new FormControl<ProjectStatus>(PROJECT_STATUS_TYPES.inactive, { nonNullable: true }),
    image: new FormControl<string | null>(null),
    plannedStart: new FormControl<Date | null>(null, { validators: [ Validators.required ] }),
    plannedEnd: new FormControl<Date | null>(null, { validators: [ Validators.required ] }),
    baseData: this._formBuilder.group({
      costCenterNumber: new FormControl<string | null>(null, { validators: [ Validators.required ] }),
      buildingOwner: new FormControl<string | null>(null),
      costCenterRegion: new FormControl<CostCenterRegion | null>(null, { validators: [ Validators.required ] }),
      shortName: new FormControl<string | null>(null),
      emails: this._formBuilder.array<FormControl<string>>([ new FormControl<string>("", {
        nonNullable: true,
        validators: [ Validators.email ]
      }) ])
    }),
    address: this._formBuilder.group({
      zipCode: new FormControl("", { nonNullable: true, validators: [ Validators.required ] }),
      city: new FormControl("", { nonNullable: true, validators: [ Validators.required ] }),
      country: new FormControl<Country | null>(null, { validators: [ Validators.required ] }),
      region: new FormControl<RegionDto | null>(null),
      streets: this._formBuilder.array<FormControl<string>>([ new FormControl<string>("", {
        nonNullable: true,
        validators: [ Validators.required ]
      }) ])
    })
  });
  /* eslint-enable @tseslint/unbound-method */

  protected selectedCountry: Signal<Country | null> = toSignal(this.form.controls.address.controls.country.valueChanges, { initialValue: null });

  protected get baseDataGroup (): FormGroup {
    return this.form.controls.baseData;
  }


  constructor () {
    this._faIconLibrary.addIcons(faLock, faPlus, faXmark, faMinus);
    this.form.addValidators(this.dateRangeValidator);
    this.getProjectDetails();
    this._translationStoreService.getLocale$()
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(isEqual)
        // ThrottleTime(1000, undefined, { leading: true, trailing: true })
      )
      .subscribe(() => {
        this.getProjectDetails();
      });
    toObservable(this.wasCreated)
      .pipe(
        filter(Boolean)
      )
      .subscribe(() => {
        this._toastService.showToast(STATUS_TYPES.success, {
          defaultText: 'Projekt erfolgreich erstellt.',
          token: 'project.project-created-success',
          scope: TRANSLATION_SCOPE
        });
      });
  }

  private getProjectDetails (): void {
    if (!this._projectNumber || this._projectNumber === "new")
      return;

    this._formStateService.clear();
    this._detailsDataService.getProjectDetails(this._projectNumber)
      .subscribe((projectDetails: ProjectDetails) => {
        const formUpdate: ProjectDetailsForm = parseToProjectDetailsForm(projectDetails);

        this.form.controls.address.setControl("streets", buildStreetsArray(formUpdate.address.streets));

        if (projectDetails.baseData.emails)
          this.form.controls.baseData.setControl("emails", buildEmailsArray(formUpdate.baseData.emails));

        this.form.patchValue(formUpdate);
      });
  }

  protected getCostCenterRegions: BaseSelectGetItems<CostCenterRegion>
    = (params: PageableFetchOptions) => this.lookup.fetch<CostCenterRegion>('projects', 'cost-center-regions', params.searchTerm, params.offset);


  protected discardChanges (): void {
    this._formStateService.clear();
    this.getProjectDetails();
  }

  protected saveChanges (): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();

      return;
    }


    const projectUpdate: ProjectDetails = parseProjectDetailsFromForm(this.form.getRawValue());

    if (this._projectNumber === "new") {
      this._detailsDataService.createProject(projectUpdate)
        .subscribe({
          next: (projectNumber: string) => {
            this._formStateService.clear();

            const urlTree: UrlTree = this._router.createUrlTree([ `projects/${ projectNumber }/settings/details` ], { queryParams: { wasCreated: true } });

            void this._router.navigateByUrl(urlTree, { onSameUrlNavigation: 'reload' })
              .then(() => {
                // Todo: reload state instead
                window.location.reload();
              });
          },
          error: () => {
            this._toastService.showToast(STATUS_TYPES.error, {
              defaultText: 'Speichern der Projektdetails fehlgeschlagen.',
              token: 'project.project-details.save-error',
              scope: TRANSLATION_SCOPE
            });
          }
        });

      return;
    }


    this._detailsDataService.updateProjectDetails(this._projectNumber, projectUpdate)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          finalize: () => {
            this.getProjectDetails();
            this._projectHeaderService.reload();
          }
        })
      )
      .subscribe({
        next: () => {
          this._toastService.showToast(STATUS_TYPES.success, {
            defaultText: 'Projektdetails erfolgreich aktualisiert.',
            token: 'project.project-details.saved',
            scope: TRANSLATION_SCOPE
          });
        },
        error: () => {
          this._toastService.showToast(STATUS_TYPES.error, {
            defaultText: 'Speichern der Projektdetails fehlgeschlagen.',
            token: 'project.project-details.save-error',
            scope: TRANSLATION_SCOPE
          });
        }
      });
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  private readonly dateRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const group: GroupOf<ProjectFormValue> = control as GroupOf<ProjectFormValue>;
    const start: Date | null = group.controls.plannedStart.value;
    const end: Date | null = group.controls.plannedEnd.value;

    if (!start || !end) return null;

    return new Date(end) >= new Date(start) ? null : { dateRange: true };
  };
}
